import { jwtDecode } from 'jwt-decode';
import { z } from 'zod';

/**
 * Custom claims stamped on the access token by the tenant's Auth0 actions —
 * the same namespace and shape the legacy portal's `parseProfile` reads.
 * Nothing needs porting: those actions are tenant-wide, and this app shares the
 * portal's Auth0 client (ADR 015), so the claims arrive already stamped.
 * FOR-2585 verifies this schema against a decoded dev token. Zod makes a
 * missing claim a loud, named failure instead of a silent `undefined`.
 */
const CLAIMS = 'https://identity.signaladvisors.com/claims';

/**
 * `company` and `company_type` are OPTIONAL deliberately, and `.nullish()`
 * rather than `.optional()` for the same reason as `role` below: the claims
 * action forwards `user_metadata` wholesale and guarantees no key inside it,
 * so a real dev-tenant user can reach this schema with the key absent
 * entirely OR present and `null`. The legacy portal declares both required on
 * `IUserMetadata` but validates nothing at runtime, so production has always
 * tolerated their absence — "required" was never true there, merely
 * unchecked. They are display fields; the load-bearing identifiers
 * (`firm_id`, `crm_id`) ARE guaranteed, because the action denies the login
 * outright when they are missing.
 */
const userMetadataSchema = z.object({
  first_name: z.string(),
  last_name: z.string(),
  company: z.string().nullish(),
  company_type: z.string().nullish(),
});

const baseClaimsSchema = z.object({
  [`${CLAIMS}/email`]: z.string(),
  [`${CLAIMS}/firm_id`]: z.string(),
  /**
   * Read from THIS namespaced access-token claim, which is the only place a
   * SPA can get it — app_metadata is Management-API-only and never reaches the
   * browser. Do not switch this to the token's bare top-level `role`: that one
   * is the constant `"authenticated"`, set by post_login_add_authenticated_role
   * for Neon's Postgres RLS role switching, so every user would read as
   * `"authenticated"`. The ID token carries no role claim at all.
   *
   * `.nullish()`, not `.optional()`. Tenant-side, the action sources this from
   * `app_metadata.role || null`, so for a user with no role the claim arrives
   * PRESENT and null rather than absent — and `.optional()` permits only
   * absence, so it would still throw on exactly the case this relaxes.
   * `.nullish()` covers both, since an impersonated `log_in_as` block could
   * omit the key entirely.
   *
   * Authorization does not depend on this today: `permissions` ships empty
   * (RBAC is on but defines no roles) and nothing reads `profile.role` yet.
   * The planned `isAuthorized(role)` guard is what will have to decide what a
   * null role MEANS — deny, or a default tier — and that belongs with the
   * native-RBAC migration, not with a zod annotation. See FOR-2585.
   */
  [`${CLAIMS}/role`]: z.string().nullish(),
  [`${CLAIMS}/user_id`]: z.string(),
  [`${CLAIMS}/username`]: z.string(),
  /**
   * `contact:account`, colon-joined CRM ids. The shape is enforced here rather
   * than tolerated in `toProfile`: a missing, empty, or extra component would
   * otherwise fall through the destructure as an empty id and reach callers as
   * a plausible-looking profile. Applies to `log_in_as` too, which reuses this
   * schema.
   */
  [`${CLAIMS}/crm_id`]: z
    .string()
    .regex(/^[^:]+:[^:]+$/, { error: 'crm_id must be `contact:account`' }),
  [`${CLAIMS}/user_metadata`]: userMetadataSchema,
  /** Optional: stamped per-flag by Auth0 actions; absence means "not set". */
  [`${CLAIMS}/feature_flags`]: z.record(z.string(), z.boolean()).optional(),
});

const tokenPayloadSchema = baseClaimsSchema.extend({
  /** Present when the user is impersonating another user. */
  [`${CLAIMS}/log_in_as`]: baseClaimsSchema.optional(),
});

type BaseClaims = z.infer<typeof baseClaimsSchema>;

export interface AuthImpersonation {
  user_id: string;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  firm_id: string;
}

export interface AuthProfile {
  email: string;
  firm_id: string;
  user_id: string;
  /**
   * Absent when the tenant has no role for the user — the claim arrives null.
   * Normalized to `undefined` so consumers handle one absent-representation
   * rather than both null and undefined.
   */
  role?: string;
  first_name: string;
  last_name: string;
  /**
   * Absent when the user's `user_metadata` omits it, or when it arrives
   * present and null — see `userMetadataSchema`. Normalized to `undefined`
   * so consumers handle one absent-representation rather than both.
   */
  company?: string;
  /**
   * Absent when the user's `user_metadata` omits it, or when it arrives
   * present and null — see `userMetadataSchema`. Normalized to `undefined`
   * so consumers handle one absent-representation rather than both.
   */
  company_type?: string;
  crm_contact_id: string;
  crm_account_id: string;
  feature_flags?: Record<string, boolean>;
  /** Set when a staff user is logged in as this profile's user. */
  impersonation?: AuthImpersonation;
}

function toProfile(claims: BaseClaims): Omit<AuthProfile, 'impersonation'> {
  const metadata = claims[`${CLAIMS}/user_metadata`];
  const [crmContactId = '', crmAccountId = ''] = claims[`${CLAIMS}/crm_id`].split(':');
  return {
    email: claims[`${CLAIMS}/email`],
    firm_id: claims[`${CLAIMS}/firm_id`],
    user_id: claims[`${CLAIMS}/user_id`],
    // null (no role in app_metadata) collapses to undefined — see AuthProfile.
    role: claims[`${CLAIMS}/role`] ?? undefined,
    first_name: metadata.first_name,
    last_name: metadata.last_name,
    // null (absent from user_metadata) collapses to undefined — see AuthProfile.
    company: metadata.company ?? undefined,
    company_type: metadata.company_type ?? undefined,
    crm_contact_id: crmContactId,
    crm_account_id: crmAccountId,
    feature_flags: claims[`${CLAIMS}/feature_flags`],
  };
}

/**
 * Decode + validate the access token's custom claims into the app profile.
 * When the `log_in_as` claim is present the user is impersonating: the
 * effective profile is the impersonated user, and the actor is surfaced on
 * `impersonation` — mirroring the legacy portal's behavior.
 *
 * Throws (zod) when a required claim is missing or malformed — startup-loud
 * by design, the same philosophy as the app's env validation.
 */
export function parseProfile(accessToken: string): AuthProfile {
  const decoded = tokenPayloadSchema.safeParse(jwtDecode(accessToken));
  if (!decoded.success) {
    // Name the offending claims. A raw ZodError's message is a JSON blob that
    // console-truncates, which is the difference between "role is null" and an
    // unreadable line — and this message IS the claim-contract evidence
    // FOR-2585 needs, so it has to survive the trip to a console.
    const claims = decoded.error.issues
      .map((issue) => `${issue.path.join('.') || '(root)'} — ${issue.message}`)
      .join('; ');
    throw new Error(`Access token claims failed validation: ${claims}`);
  }
  const payload = decoded.data;
  const logInAs = payload[`${CLAIMS}/log_in_as`];

  if (!logInAs) {
    return toProfile(payload);
  }

  const actorMetadata = payload[`${CLAIMS}/user_metadata`];
  return {
    ...toProfile(logInAs),
    impersonation: {
      user_id: payload[`${CLAIMS}/user_id`],
      username: payload[`${CLAIMS}/username`],
      first_name: actorMetadata.first_name,
      last_name: actorMetadata.last_name,
      email: payload[`${CLAIMS}/email`],
      firm_id: payload[`${CLAIMS}/firm_id`],
    },
  };
}
